import {
  getLinkWithLanguageAndEnvironment,
  getLinkWithLanguage,
} from "./urlHelpers";
import {
  CoverageType,
  PlanType,
  PrescriptionFrequency,
  LowIncomeSubsidyOption,
  SortOptions,
  UserLanguage,
  DrugPricingRedactionIssue,
  PlanBenefitRedactionIssue,
  WhatToDoType,
} from "../@types";
import { TranslationKey } from "./intlHooks";

/** Constant representing Part B premium values where required to be hardcoded */
export const partBPremium = {
  "2022": 170.1,
  "2023": 164.9,
  "2024": 174.7,
  "2025": 174.7,
};

/** PartD Out of Pocket Threshold */
export const partDOutOfPocket = {
  "2024": 8000,
  "2025": 2000,
};

/** ----- Used for MEDIGAP messaging ----- */
/**
 * The values below work to update Medigap plan-card, plan-details, and help-drawer
 * messaging each year. Unless otherwise called out, no Phrase keys should need
 * to be changed.
 */

/**
 * Part B Health Deductible, by year
 * For Medigap, this value (or a percentage of it) is used across plans, in both
 * waiver- and non-waiver state messaging
 */
export const partBHealthDeductible = {
  "2022": 233,
  "2023": 226,
  "2024": 240,
};

/**
 * Part A Health Deductible, by year
 * For Medigap, this value (or a percentage of it) is used across plans, in both
 * waiver- and non-waiver state messaging
 */
export const partAHealthDeductible = {
  "2022": 1556,
  "2023": 1600,
  "2024": 1632,
};

/**
 * Skilled Nursing Facility costs for days 21-100, by year
 * This value (or a percentage of it) is used across plans, in both waiver- and
 * non-waiver state messaging
 */
export const medigapSnfDays21to100 = {
  "2022": 194,
  "2023": 200,
  "2024": 204,
};

/** Plan K Part B Coinsurance OOP limits, by year */
export const medigapPlanKOutOfPocketLimit = {
  "2022": 6620,
  "2023": 6940,
  "2024": 7060,
};

/**
 * Plan L Part B Coinsurance OOP limits, by year
 * (may or may not match those for Plan K / 2?)
 */
export const medigapPlanLOutOfPocketLimit = {
  "2022": medigapPlanKOutOfPocketLimit["2022"] / 2,
  "2023": medigapPlanKOutOfPocketLimit["2023"] / 2,
  "2024": medigapPlanKOutOfPocketLimit["2024"] / 2,
};

/** High deductible Plan F deductibles, by year */
export const medigapHighFDeductible = {
  "2022": 2490,
  "2023": 2700,
  "2024": 2800,
};

/** High deductible Plan G deductibles, by year (may or may not differ from Plan F) */
export const medigapHighGDeductible = {
  "2022": medigapHighFDeductible["2022"],
  "2023": medigapHighFDeductible["2023"],
  "2024": medigapHighFDeductible["2024"],
};

/** ----- end for MEDIGAP messaging ----- */

const pagination = {
  maxNumDisplayAllPages: 7,
};

/** Used to determine if a plan has at least some dental coverage */
const dentalServices = [
  "diagnostic_services",
  "endodontics",
  "extractions",
  "nonroutine_services",
  "periodontics",
  "prosthodontics_and_other_services",
  "restorative_services",
];

/* Enrollment-related vars */
const langChars = 2;
/* istanbul ignore next */
const language =
  navigator.language.substring(0, langChars) === "es" ? "Spanish" : "English";

/**
 * @property count: The 'count' of how many results to return per page when querying the API.
 */
const apiVars = {
  count: 10,
};

/**
 * @property maxPlansToCompare: The maximum allowed number of plans that can be compared.
 */
const compareFooter = {
  minPlansToCompare: 2,
  maxPlansToCompare: 3,
  errorTimeout: 3000,
};

/**
 * Pharmacy selection constants
 */
const pharmacySelection = {
  min: 0,
  max: 5,
  pageCount: 10,
  maxPageCount: 40,
  maxPharmacyRequests: 200,
};

/**
 * A mapping of sort-by options for the [[SearchResultsPage]]. Holds the PhraseApp ID as the value.
 */
const sortOptionsLabels = {
  [SortOptions.DRUG_PLAN_DEDUCTIBLE]:
    "search_results_page.sort.lowest_annual_drug_deductible",
  [SortOptions.ANNUAL_DRUGS_TOTAL]:
    "search_results_page.sort.lowest_annual_drug_total",
  [SortOptions.ANNUAL_DEDUCTIBLE]:
    "search_results_page.sort.lowest_health_plan_deductible",
  [SortOptions.MONTHLY_PREMIUM]:
    "search_results_page.sort.lowest_monthly_premium",
  [SortOptions.ANNUAL_TOTAL]:
    "search_results_page.sort.lowest_drug_plus_premium_cost",
};

const sortOptionsLabelsByPlan: {
  [x: string]: Array<{ [x: string]: string }>;
} = {
  [PlanType.PDP]: [
    {
      [SortOptions.DRUG_PLAN_DEDUCTIBLE]:
        sortOptionsLabels.DRUG_PLAN_DEDUCTIBLE,
    },
    {
      [SortOptions.ANNUAL_TOTAL]: sortOptionsLabels.ANNUAL_TOTAL,
    },
    {
      [SortOptions.MONTHLY_PREMIUM]: sortOptionsLabels.MONTHLY_PREMIUM,
    },
  ],
  [PlanType.MAPD]: [
    {
      [SortOptions.DRUG_PLAN_DEDUCTIBLE]:
        sortOptionsLabels.DRUG_PLAN_DEDUCTIBLE,
    },
    {
      [SortOptions.ANNUAL_DEDUCTIBLE]: sortOptionsLabels.ANNUAL_DEDUCTIBLE,
    },
    {
      [SortOptions.ANNUAL_TOTAL]: sortOptionsLabels.ANNUAL_TOTAL,
    },
    {
      [SortOptions.MONTHLY_PREMIUM]: sortOptionsLabels.MONTHLY_PREMIUM,
    },
  ],
};

const prescriptionFrequencyLabels: Record<
  PrescriptionFrequency,
  TranslationKey
> = {
  [PrescriptionFrequency.DAYS_30]: "prescription.frequency.days_30",
  [PrescriptionFrequency.DAYS_60]: "prescription.frequency.days_60",
  [PrescriptionFrequency.DAYS_90]: "prescription.frequency.days_90",
  [PrescriptionFrequency.DAYS_180]: "prescription.frequency.days_180",
  [PrescriptionFrequency.DAYS_360]: "prescription.frequency.days_360",
};

const coverageWizardUrl = (language: UserLanguage): string => {
  let origin = window.location.origin;
  origin = origin.replace(/localhost/, "dev1.medicare.gov");
  if (language === UserLanguage.SPANISH) {
    origin = origin.replace(/www/, "es");
    origin = origin.replace(/imp/, "es.imp");
  }
  return `${origin}/medicarecoverageoptions/#home`;
};

const abstractBenefitTierNames = {
  COST_SHARE_TIER_0_DOLLAR_DRUGS: "COST_SHARE_TIER_0_DOLLAR_DRUGS",
  COST_SHARE_TIER_BRAND_DRUGS: "COST_SHARE_TIER_BRAND_DRUGS",
  COST_SHARE_TIER_BRAND_NAME_DRUGS: "COST_SHARE_TIER_BRAND_NAME_DRUGS",
  COST_SHARE_TIER_BRAND: "COST_SHARE_TIER_BRAND",
  COST_SHARE_TIER_EXCLUDED_DRUGS: "COST_SHARE_TIER_EXCLUDED_DRUGS",
  COST_SHARE_TIER_GENERIC_DRUGS: "COST_SHARE_TIER_GENERIC_DRUGS",
  COST_SHARE_TIER_GENERIC: "COST_SHARE_TIER_GENERIC",
  COST_SHARE_TIER_INJECTABLE_DRUGS: "COST_SHARE_TIER_INJECTABLE_DRUGS",
  COST_SHARE_TIER_NON_MEDICARE_OTC_DRUGS:
    "COST_SHARE_TIER_NON_MEDICARE_OTC_DRUGS",
  COST_SHARE_TIER_NON_MEDICARE_RX_DRUGS:
    "COST_SHARE_TIER_NON_MEDICARE_RX_DRUGS",
  COST_SHARE_TIER_NON_MEDICARE_RX_OTC_DRUGS:
    "COST_SHARE_TIER_NON_MEDICARE_RX_OTC_DRUGS",
  COST_SHARE_TIER_NON_PREFERRED_BRAND_DRUGS:
    "COST_SHARE_TIER_NON_PREFERRED_BRAND_DRUGS",
  COST_SHARE_TIER_NON_PREFERRED_BRAND: "COST_SHARE_TIER_NON_PREFERRED_BRAND",
  COST_SHARE_TIER_NON_PREFERRED_DRUG: "COST_SHARE_TIER_NON_PREFERRED_DRUG",
  COST_SHARE_TIER_PREFERRED_BRAND_DRUGS:
    "COST_SHARE_TIER_PREFERRED_BRAND_DRUGS",
  COST_SHARE_TIER_PREFERRED_BRAND: "COST_SHARE_TIER_PREFERRED_BRAND",
  COST_SHARE_TIER_PREFERRED_GENERIC_DRUGS:
    "COST_SHARE_TIER_PREFERRED_GENERIC_DRUGS",
  COST_SHARE_TIER_PREFERRED_GENERIC: "COST_SHARE_TIER_PREFERRED_GENERIC",
  COST_SHARE_TIER_PREFERRED_SPECIALTY_TIER:
    "COST_SHARE_TIER_PREFERRED_SPECIALTY_TIER",
  COST_SHARE_TIER_SELECT_CARE_DRUGS: "COST_SHARE_TIER_SELECT_CARE_DRUGS",
  COST_SHARE_TIER_SELECT_DIABETIC_DRUGS:
    "COST_SHARE_TIER_SELECT_DIABETIC_DRUGS",
  COST_SHARE_TIER_SPECIALTY_TIER: "COST_SHARE_TIER_SPECIALTY_TIER",
  COST_SHARE_TIER_SUPPLEMENTAL_BRAND_AND_GENERIC_DRUGS:
    "COST_SHARE_TIER_SUPPLEMENTAL_BRAND_AND_GENERIC_DRUGS",
  COST_SHARE_TIER_SUPPLEMENTAL_DRUGS: "COST_SHARE_TIER_SUPPLEMENTAL_DRUGS",
  COST_SHARE_TIER_VACCINES: "COST_SHARE_TIER_VACCINES",
};

const abstractBenefitTierTranslations = {
  [abstractBenefitTierNames.COST_SHARE_TIER_0_DOLLAR_DRUGS]:
    "abstract_benefits.cost_share_tier.0_dollar_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_BRAND_DRUGS]:
    "abstract_benefits.cost_share_tier.brand_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_BRAND_NAME_DRUGS]:
    "abstract_benefits.cost_share_tier.brand_name_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_BRAND]:
    "abstract_benefits.cost_share_tier.brand",
  [abstractBenefitTierNames.COST_SHARE_TIER_EXCLUDED_DRUGS]:
    "abstract_benefits.cost_share_tier.excluded_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_GENERIC_DRUGS]:
    "abstract_benefits.cost_share_tier.generic_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_GENERIC]:
    "abstract_benefits.cost_share_tier.generic",
  [abstractBenefitTierNames.COST_SHARE_TIER_INJECTABLE_DRUGS]:
    "abstract_benefits.cost_share_tier.injectable_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_NON_MEDICARE_OTC_DRUGS]:
    "abstract_benefits.cost_share_tier.non_medicare_otc_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_NON_MEDICARE_RX_DRUGS]:
    "abstract_benefits.cost_share_tier.non_medicare_rx_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_NON_MEDICARE_RX_OTC_DRUGS]:
    "abstract_benefits.cost_share_tier.non_medicare_rx_otc_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_NON_PREFERRED_BRAND_DRUGS]:
    "abstract_benefits.cost_share_tier.non_preferred_brand_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_NON_PREFERRED_BRAND]:
    "abstract_benefits.cost_share_tier.non_preferred_brand",
  [abstractBenefitTierNames.COST_SHARE_TIER_NON_PREFERRED_DRUG]:
    "abstract_benefits.cost_share_tier.non_preferred_drug",
  [abstractBenefitTierNames.COST_SHARE_TIER_PREFERRED_BRAND_DRUGS]:
    "abstract_benefits.cost_share_tier.preferred_brand_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_PREFERRED_BRAND]:
    "abstract_benefits.cost_share_tier.preferred_brand",
  [abstractBenefitTierNames.COST_SHARE_TIER_PREFERRED_GENERIC_DRUGS]:
    "abstract_benefits.cost_share_tier.preferred_generic_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_PREFERRED_GENERIC]:
    "abstract_benefits.cost_share_tier.preferred_generic",
  [abstractBenefitTierNames.COST_SHARE_TIER_PREFERRED_SPECIALTY_TIER]:
    "abstract_benefits.cost_share_tier.preferred_specialty_tier",
  [abstractBenefitTierNames.COST_SHARE_TIER_SELECT_CARE_DRUGS]:
    "abstract_benefits.cost_share_tier.select_care_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_SELECT_DIABETIC_DRUGS]:
    "abstract_benefits.cost_share_tier.select_diabetic_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_SPECIALTY_TIER]:
    "abstract_benefits.cost_share_tier.specialty_tier",
  [abstractBenefitTierNames.COST_SHARE_TIER_SUPPLEMENTAL_BRAND_AND_GENERIC_DRUGS]:
    "abstract_benefits.cost_share_tier.supplemental_brand_and_generic_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_SUPPLEMENTAL_DRUGS]:
    "abstract_benefits.cost_share_tier.supplemental_drugs",
  [abstractBenefitTierNames.COST_SHARE_TIER_VACCINES]:
    "abstract_benefits.cost_share_tier.vaccines",
};

const optionalPackageBenefits = {
  SUPPLEMENTAL_NOT_AVAILABLE: "optional_packages.benefit.not_available",
  SUPPLEMENTAL_INPATIENT_HOSPITAL_COVERAGE:
    "optional_packages.benefit.inpatient_hospital_coverage",
  SUPPLEMENTAL_MENTAL_HEALTH_SERVICES:
    "optional_packages.benefit.mental_health_services",
  SUPPLEMENTAL_SKILLED_NURSING_FACILITY:
    "optional_packages.benefit.skilled_nursing_facility",
  SUPPLEMENTAL_CARDIAC_REHABILITATION_SERVICES:
    "optional_packages.benefit.cardiac_rehabilitation_services",
  SUPPLEMENTAL_INTENSIVE_CARDIAC_REHABILITATION_SERVICES:
    "optional_packages.benefit.intensive_cardiac_rehabilitation_services",
  SUPPLEMENTAL_PULMONARY_REHABILITATION_SERVICES:
    "optional_packages.benefit.pulmonary_rehabilitation_services",
  SUPPLEMENTAL_EMERGENCY: "optional_packages.benefit.emergency",
  SUPPLEMENTAL_URGENT_CARE: "optional_packages.benefit.urgent_care",
  SUPPLEMENTAL_WORLDWIDE_EMERGENCY:
    "optional_packages.benefit.worldwide_emergency",
  SUPPLEMENTAL_PARTIAL_HOSPITALIZATION:
    "optional_packages.benefit.partial_hospitalization",
  SUPPLEMENTAL_HOME_HEALTH_CARE: "optional_packages.benefit.home_health_care",
  SUPPLEMENTAL_PRIMARY: "optional_packages.benefit.primary",
  SUPPLEMENTAL_CHIROPRACTIC_CARE: "optional_packages.benefit.chiropractic_care",
  SUPPLEMENTAL_OCCUPATIONAL_THERAPY:
    "optional_packages.benefit.occupational_therapy",
  SUPPLEMENTAL_SPECIALIST: "optional_packages.benefit.specialist",
  SUPPLEMENTAL_OUTPATIENT_THERAPY:
    "optional_packages.benefit.outpatient_therapy",
  SUPPLEMENTAL_FOOT_CARE: "optional_packages.benefit.foot_care",
  SUPPLEMENTAL_OTHER_HEALTH_CARE_PROFESSIONAL:
    "optional_packages.benefit.other_health_care_professional",
  SUPPLEMENTAL_OUTPATIENT_THERAPY_PSYCHIATRIST:
    "optional_packages.benefit.outpatient_therapy_psychiatrist",
  SUPPLEMENTAL_PHYSICAL_SPEECH_LANGUAGE_THERAPY_VISIT:
    "optional_packages.benefit.physical_speech_language_therapy_visit",
  SUPPLEMENTAL_DIAGNOSTIC_PROCEDURES:
    "optional_packages.benefit.diagnostic_procedures",
  SUPPLEMENTAL_DIAGNOSTIC_RADIOLOGY:
    "optional_packages.benefit.diagnostic_radiology",
  SUPPLEMENTAL_OUTPATIENT_HOSPITAL:
    "optional_packages.benefit.outpatient_hospital",
  SUPPLEMENTAL_AMBULATORY_SURGICAL_CENTER:
    "optional_packages.benefit.ambulatory_surgical_center",
  SUPPLEMENTAL_OUTPATIENT_SUBSTANCE_ABUSE:
    "optional_packages.benefit.outpatient_substance_abuse",
  SUPPLEMENTAL_BLOOD: "optional_packages.benefit.blood",
  SUPPLEMENTAL_AMBULANCE: "optional_packages.benefit.ambulance",
  SUPPLEMENTAL_TRANSPORTATION: "optional_packages.benefit.transportation",
  SUPPLEMENTAL_DURABLE_MEDICAL_EQUIPMENT:
    "optional_packages.benefit.durable_medical_equipment",
  SUPPLEMENTAL_PROSTHETICS: "optional_packages.benefit.prosthetics",
  SUPPLEMENTAL_DIABETES_SUPPLIES: "optional_packages.benefit.diabetes_supplies",
  SUPPLEMENTAL_RENAL_DIALYSIS: "optional_packages.benefit.renal_dialysis",
  SUPPLEMENTAL_ACUPUNCTURE: "optional_packages.benefit.acupuncture",
  SUPPLEMENTAL_OVER_THE_COUNTER_ITEMS:
    "optional_packages.benefit.over_the_counter_items",
  SUPPLEMENTAL_MEAL_BENEFIT: "optional_packages.benefit.meal_benefit",
  SUPPLEMENTAL_OTHER_1: "optional_packages.benefit.other_1",
  SUPPLEMENTAL_OTHER_2: "optional_packages.benefit.other_2",
  SUPPLEMENTAL_OTHER_3: "optional_packages.benefit.other_3",
  SUPPLEMENTAL_DUAL_ELIGIBLE_SNP_WITH_HIGHLY_INTEGRATED_SERVICES:
    "optional_packages.benefit.dual_eligible_snp_with_highly_integrated_services",
  SUPPLEMENTAL_PREVENTIVE_CARE: "optional_packages.benefit.preventive_care",
  SUPPLEMENTAL_ANNUAL_PHYSICAL_EXAM:
    "optional_packages.benefit.annual_physical_exam",
  SUPPLEMENTAL_WELLNESS_PROGRAMS: "optional_packages.benefit.wellness_programs",
  SUPPLEMENTAL_KIDNEY_DISEASE_EDUCATION_SERVICES:
    "optional_packages.benefit.kidney_disease_education_services",
  SUPPLEMENTAL_OTHER_MEDICARE_COVERED_PREVENTIVE_SERVICES:
    "optional_packages.benefit.other_medicare_covered_preventive_services",
  SUPPLEMENTAL_MEDICARE_PART_B_DRUGS:
    "optional_packages.benefit.medicare_part_b_drugs",
  SUPPLEMENTAL_PREVENTIVE_DENTAL_SERVICES:
    "optional_packages.benefit.preventive_dental_services",
  SUPPLEMENTAL_COMPREHENSIVE_DENTAL_SERVICES:
    "optional_packages.benefit.comprehensive_dental_services",
  SUPPLEMENTAL_EYE_EXAMS: "optional_packages.benefit.eye_exams",
  SUPPLEMENTAL_EYEWEAR: "optional_packages.benefit.eyewear",
  SUPPLEMENTAL_HEARING_EXAMS: "optional_packages.benefit.hearing_exams",
  SUPPLEMENTAL_HEARING_AIDS: "optional_packages.benefit.hearing_aids",
  SUPPLEMENTAL_PRESCRIPTION_DRUGS_COST_PLANS_ONLY:
    "optional_packages.benefit.prescription_drugs_cost_plans_only",
  SUPPLEMENTAL_POS_OPTION: "optional_packages.benefit.pos_option",
  SUPPLEMENTAL_VISITOR_TRAVEL_PROGRAM:
    "optional_packages.benefit.visitor_travel_program",
};

/**
 * For URLs that start with `medicare.gov`.
 * Create a helper like `linkTo<something>` which would call `getLinkWithLanguage`
 */
export const mGovUrls = {
  avoidPenalties: "medicare.gov/basics/costs/medicare-costs/avoid-penalties",
  avoidPartDPenalties:
    "medicare.gov/basics/costs/medicare-costs/avoid-penalties#part-D-LEP",
  callMedicare: "1-800-633-4227",
  contacts: "medicare.gov/contacts",
  costHelp: "medicare.gov/basics/costs/help",
  coverageWizard: "medicare.gov/medicarecoverageoptions/",
  drugCoveragePartD: "medicare.gov/drug-coverage-part-d",
  endStageRenalDisease: "medicare.gov/basics/end-stage-renal-disease",
  getHelpPayingCosts: "medicare.gov/basics/costs/help",
  getStartedWithMedicare: "medicare.gov/basics/get-started-with-medicare",
  howDoMedicareAdvantagePlansWork:
    "medicare.gov/health-drug-plans/health-plans",
  howOriginalMedicareWorks:
    "medicare.gov/what-medicare-covers/your-medicare-coverage-choices/how-original-medicare-works",
  joiningAPlan:
    "medicare.gov/sign-up-change-plans/joining-a-health-or-drug-plan",
  lowerPrescriptionCosts: "medicare.gov/basics/costs/help/drug-costs",
  medicareAndYou: "medicare.gov/medicare-and-you",
  medicareHome: "medicare.gov",
  medigapBasics: "medicare.gov/health-drug-plans/medigap/basics",
  medigapGuaranteedIssueRights:
    "medicare.gov/supplements-other-insurance/when-can-i-buy-medigap/guaranteed-issue-rights",
  medigap: "medicare.gov/health-drug-plans/medigap",
  medigapInfo:
    "medicare.gov/supplements-other-insurance/whats-medicare-supplement-insurance-medigap",
  medigapCostsOfPolicies: "medicare.gov/health-drug-plans/medigap/basics/costs",
  medigapStateInsuranceDepartments: "medicare.gov/Contacts/#resources/sids",
  medigapWaiverStateInfo:
    "medicare.gov/supplements-other-insurance/how-to-compare-medigap-policies/medigap-in-",
  paceHome: "medicare.gov/pace",
  paceInfo:
    "medicare.gov/health-drug-plans/health-plans/your-coverage-options/other-medicare-health-plans/PACE",
  partDCoPaymentCoInsurance:
    "medicare.gov/drug-coverage-part-d/costs-for-medicare-drug-coverage/copaymentcoinsurance-in-drug-plans",
  partDLateEnrollmentPenalty:
    "medicare.gov/drug-coverage-part-d/costs-for-medicare-drug-coverage/part-d-late-enrollment-penalty",
  prescriptionCostHelp: "medicare.gov/basics/costs/help/drug-costs",
  prescriptionPaymentPlan: "medicare.gov/prescription-payment-plan",
  snp: "medicare.gov/health-drug-plans/health-plans/your-coverage-options/SNP",
  spap: "medicare.gov/pharmaceutical-assistance-program/#state-programs",
  secondaryCallMedicare: "1-877-486-2048",
  specialEnrollmentPeriodInfo:
    "medicare.gov/basics/get-started-with-medicare/get-more-coverage/joining-a-plan",
  talkToSomeone: "medicare.gov/talk-to-someone",
  typesOfHealthPlans:
    "medicare.gov/sign-up-change-plans/different-types-of-medicare-health-plans",
  whatIsMedicareMSA:
    "medicare.gov/health-drug-plans/health-plans/your-coverage-options/MSA",
  whatMedicareCovers: "medicare.gov/what-medicare-covers",
  whenMedicareStarts:
    "medicare.gov/basics/get-started-with-medicare/sign-up/when-does-medicare-coverage-start",
  whenCanISignUpForMedicare:
    "medicare.gov/basics/get-started-with-medicare/sign-up/when-can-i-sign-up-for-medicare",
} as const;

/**
 * For URLs that start with `www.ssa.gov`.
 */
export const ssaGovUrls = {
  /**
   * Social Security Disability Insurance (SSDI) Benefit website
   */
  ssdiBenefits: {
    [UserLanguage.ENGLISH]: "https://www.ssa.gov/benefits/disability/",
    [UserLanguage.SPANISH]:
      "https://www.ssa.gov/espanol/beneficios/incapacidad/",
  },
};

export const linkToSHIPHelp = "https://www.shiphelp.org/";
export const linkToPaxlovidProgram = "https://www.paxlovid.com/paxcess";

const authenticatedMGovUrls = {
  dashboard: "medicare.gov/my/home",
};

export const maxLisDrugDeductibles = {
  "2020": 89,
  "2021": 92,
  "2022": 99,
  "2023": 104,
};

export const medigapLink =
  "https://www.medicare.gov/medigap-supplemental-insurance-plans";

export const linkToPartDLateEnrollmentPenalty = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.partDLateEnrollmentPenalty, language);
};

export const linkToPartDCoPaymentCoInsurance = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.partDCoPaymentCoInsurance, language);
};

export const linkToContacts = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.contacts, language);
};

export const linkToCostHelp = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.costHelp, language);
};

export const linkToCoverageWizard = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.coverageWizard, language);
};

export const linkToDrugCoveragePartD = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.drugCoveragePartD, language);
};

export const linkToGetHelpPayingCosts = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.getHelpPayingCosts, language);
};

export const linkToGetStartedWithMedicare = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.getStartedWithMedicare, language);
};

export const linkToYourCoverageOptions = (language: UserLanguage): string => {
  return getLinkWithLanguage(
    `${mGovUrls.getStartedWithMedicare}/get-more-coverage/your-coverage-options`,
    language
  );
};

export const linkToHowDoMedicareAdvantagePlansWork = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(
    mGovUrls.howDoMedicareAdvantagePlansWork,
    language
  );
};

export const linkToHowOriginalMedicareWorks = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.howOriginalMedicareWorks, language);
};

export const linkToJoiningAPlan = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.joiningAPlan, language);
};

export const linkToLowerPrescriptionCosts = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.lowerPrescriptionCosts, language);
};

export const linkToPaceHome = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.paceHome, language);
};

export const linkToPaceInfo = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.paceInfo, language);
};

export const linkToAccountHome = (language: UserLanguage): string => {
  return getLinkWithLanguageAndEnvironment(
    authenticatedMGovUrls.dashboard,
    language
  );
};

export const linkToMyPlans = (language: UserLanguage): string => {
  return getLinkWithLanguageAndEnvironment(
    authenticatedMGovUrls.dashboard,
    language
  );
};

export const linkToMedicareAndYou = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.medicareAndYou, language);
};

export const linkToMedicareHome = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.medicareHome, language);
};

export const linkToPrescriptionCostHelp = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.prescriptionCostHelp, language);
};

export const linkToSpecialEnrollmentPeriodInfo = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.specialEnrollmentPeriodInfo, language);
};

export const linkToMedigapBasics = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.medigapBasics, language);
};

export const linkToMedigapGuaranteedIssueRights = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.medigapGuaranteedIssueRights, language);
};

export const linkToMedigapInfo = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.medigapInfo, language);
};

export const linkToMedigapCostsOfPolicies = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(mGovUrls.medigapCostsOfPolicies, language);
};

export const linkToMedigapStateInsuranceDepartments = (
  language: UserLanguage
): string => {
  return getLinkWithLanguage(
    mGovUrls.medigapStateInsuranceDepartments,
    language
  );
};

export const linkToMedigapWaiverStateInfo = (
  language: UserLanguage,
  state: string
): string => {
  return getLinkWithLanguage(mGovUrls.medigapWaiverStateInfo + state, language);
};

export const linkToTypesOfHealthPlans = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.typesOfHealthPlans, language);
};

export const linkToWhatMedicareCovers = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.whatMedicareCovers, language);
};

export const linkToWhenMedicareStarts = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.whenMedicareStarts, language);
};

export const linkToContactMedicare = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.talkToSomeone, language);
};

export const linkToEndStageRenalDisease = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.endStageRenalDisease, language);
};

export const linkToAvoidPenalities = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.avoidPenalties, language);
};

export const linkToSNP = (language: UserLanguage): string => {
  return getLinkWithLanguage(mGovUrls.snp, language);
};

export const getLinkToMedicare = ({
  language,
  mgovUrlKey,
}: {
  language: UserLanguage;
  mgovUrlKey: keyof typeof mGovUrls;
}) => getLinkWithLanguage(mGovUrls[mgovUrlKey], language);

export const mailOrderNpi = "mail-order";

export const emdash = "—";

export const medigapPath = "medigap-supplemental-insurance-plans";
export const planComparePath = "plan-compare";
export const slsCallbackPath = "sls-callback";

export const CONSTANTS = {
  abstractBenefitTierNames,
  abstractBenefitTierTranslations,
  apiVars,
  compareFooter,
  coverageWizardUrl,
  dentalServices,
  emdash,
  getLinkToMedicare,
  language,
  linkToAccountHome,
  linkToAvoidPenalities,
  linkToContacts,
  linkToCostHelp,
  linkToCoverageWizard,
  linkToEndStageRenalDisease,
  linkToGetHelpPayingCosts,
  linkToGetStartedWithMedicare,
  linkToYourCoverageOptions,
  linkToMedicareAndYou,
  linkToMedicareHome,
  linkToMedigapBasics,
  linkToMedigapGuaranteedIssueRights,
  linkToMedigapInfo,
  linkToMedigapStateInsuranceDepartments,
  linkToMedigapWaiverStateInfo,
  linkToMyPlans,
  linkToPaceHome,
  linkToPaceInfo,
  linkToPaxlovidProgram,
  linkToSNP,
  linkToSpecialEnrollmentPeriodInfo,
  linkToTypesOfHealthPlans,
  linkToWhenMedicareStarts,
  mailOrderNpi,
  maxLisDrugDeductibles,
  medigapLink,
  medigapPath,
  optionalPackageBenefits,
  pagination,
  partBHealthDeductible,
  partBPremium,
  pharmacySelection,
  planComparePath,
  prescriptionFrequencyLabels,
  ssaGovUrls,
  slsCallbackPath,
  sortOptionsLabels,
  sortOptionsLabelsByPlan,
};

export const TealiumConversions = {
  [LowIncomeSubsidyOption.Medicaid]: "Medicaid",
  [LowIncomeSubsidyOption.SSI]: "SSI",
  [LowIncomeSubsidyOption.MSP]: "MSP",
  [LowIncomeSubsidyOption.SS]: "Social Security",
  [LowIncomeSubsidyOption.Unsure]: "Not Sure",
  [LowIncomeSubsidyOption.None]: "Don't Get Help",
  [PlanType.MAPD]: "Medicare Advantage",
  [PlanType.PDP]: "Part D",
  [PlanType.MEDIGAP]: "Medigap",
  [CoverageType.MAPD]: "MAPD",
  [CoverageType.PDP]: "Part D",
  [CoverageType.MedigapPolicy]: "Medigap",
  // `CoverageType.learnMore` is also being used as a proxy for `WhatToDoType.learnMore`,
  // which is the same value: "LEARN_MORE"
  [CoverageType.learnMore]: "Learn More",
  [WhatToDoType.choosePlans]: "Choose Plans",
};

export const StickyNavE2EClassName = "e2e-sticky-nav";

export const DrugPricingRedactionIssues = Object.keys(
  DrugPricingRedactionIssue
) as Array<keyof typeof DrugPricingRedactionIssue>;

export const PlanBenefitRedactionIssues = Object.keys(
  PlanBenefitRedactionIssue
) as Array<keyof typeof PlanBenefitRedactionIssue>;

export const coverageTypeIdMappings = {
  [CoverageType.MAPD]: "what-coverage-mapd",
  [CoverageType.PDP]: "what-coverage-pdp",
  [CoverageType.MedigapPolicy]: "what-coverage-medigap",
};
